import React, { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import NavBar from './components/NavBar';
import { PricingForm } from './components/PricingForm';
import { AvailabilityCalendar } from './components/AvailabilityCalendar';
import { BookingForm } from './components/BookingForm';
import AboutPage from './components/AboutPage';
import CopyPricingForm from './components/CopyPricingForm';
import { LoadScript } from '@react-google-maps/api';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60, // 1 minute
      retry: 1,
      refetchOnWindowFocus: false
    },
    mutations: {
      retry: 1
    }
  },
});

const libraries: ("places" | "geometry" | "drawing" | "visualization" | "marker")[] = ["places", "marker"];

function App() {
  const [activeQuoteId, setActiveQuoteId] = useState<number | null>(null);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [selectedPort, setSelectedPort] = useState<string | null>(null);
  const [rateDetails, setRateDetails] = useState<any>(null);

  const isRatesSubdomain = window.location.hostname.startsWith('rates.');

  const handlePricingComplete = (quoteId: number, port: string, details: any) => {
    setActiveQuoteId(quoteId);
    setSelectedPort(port);
    setRateDetails(details);
    setBookingComplete(false);
    // Scroll to booking form
    setTimeout(() => {
      const bookingElement = document.getElementById('booking-section');
      if (bookingElement) {
        bookingElement.scrollIntoView({ behavior: 'smooth' });
      }
    }, 100);
  };

  const handlePortChange = (port: string) => {
    setSelectedPort(port);
  };

  const handleBookingComplete = () => {
    setBookingComplete(true);
    // Refresh availability data after successful booking
    queryClient.invalidateQueries({ queryKey: ['availability'] });
  };

  // Calculate FSC percentage
  const calculateFSCPercentage = (lineHaul: number, fscSurcharge: number): number => {
    return Math.round(lineHaul * (fscSurcharge / 100));
  };

  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <LoadScript googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || ''} libraries={libraries}>
          <div className="min-h-screen bg-gray-100 flex flex-col">
            <NavBar />
            <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8 flex-grow">
              <Routes>
                <Route path="/" element={
                  <div className="px-4 py-6 sm:px-0">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                      {/* Left Column - Pricing Form */}
                      <div>
                        <PricingForm onPricingComplete={handlePricingComplete} onPortChange={handlePortChange} />
                      </div>

                      {/* Right Column - Availability Calendar and Rate Details */}
                      <div className="lg:sticky lg:top-6">
                        <AvailabilityCalendar selectedPort={selectedPort} />
                        {rateDetails && (
                          <div className="mt-6 card bg-white shadow-lg rounded-lg p-6">
                            <div className="flex justify-between items-start mb-6">
                              <h3 className="text-2xl font-bold text-[#3B5B3C]">Rate Details</h3>
                              <div className="text-right text-sm text-gray-600">
                                <p>From: {rateDetails.terminalLocation}</p>
                                <p>To: {rateDetails.city}, {rateDetails.state} {rateDetails.zipCode}</p>
                              </div>
                            </div>

                            <div className="bg-gray-50 p-6 rounded-lg">
                              <div className="grid grid-cols-2 gap-x-8 gap-y-4">
                                {/* Base Charges */}
                                <div>
                                  <h4 className="font-bold text-gray-900">Line Haul</h4>
                                  <p className="text-2xl">${rateDetails.lineHaul}</p>
                                </div>
                                <div>
                                  <h4 className="font-semibold text-gray-700">FSC</h4>
                                  <p>{rateDetails.fscSurcharge}% (${calculateFSCPercentage(rateDetails.lineHaul, rateDetails.fscSurcharge)})</p>
                                </div>

                                {/* Layover/Mileage Surcharge */}
                                {rateDetails.mileageSurcharge > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 text-sm">Layover</h4>
                                    <p className="text-base">${rateDetails.mileageSurcharge}</p>
                                  </div>
                                )}

                                {/* Tolls */}
                                {rateDetails.tolls > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 text-sm">Tolls</h4>
                                    <p className="text-base">${rateDetails.tolls}</p>
                                  </div>
                                )}

                                {/* Overweight Details */}
                                {rateDetails.overweightDetails && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 text-sm">Overweight Charges</h4>
                                    <p className="text-base">${rateDetails.overweightTotal}</p>
                                  </div>
                                )}
                              
                                {/* HazMat Fee */}
                                {rateDetails.hazFee > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 text-sm">HazMat Fee</h4>
                                    <p className="text-base">${rateDetails.hazFee}</p>
                                  </div>
                                )}

                                {/* Bulkliquid Fee */}
                                {rateDetails.bulkLiquidFee > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 text-sm">Bulkliquid Fee</h4>
                                    <p className="text-base">${rateDetails.bulkLiquidFee}</p>
                                  </div>
                                )}

                                {/* Reefer Charge */}
                                {rateDetails.reeferFee > 0 && (
                                  <div>
                                    <h4 className="font-semibold text-gray-700 text-sm">Reefer Charge</h4>
                                    <p className="text-base">${rateDetails.reeferFee}</p>
                                  </div>
                                )}

                                <div className="col-span-2 border-t pt-4 mt-4">
                                  <h4 className="font-bold text-gray-900">Total Price</h4>
                                  <p className="text-3xl font-bold text-primary">
                                    ${rateDetails.total}
                                  </p>
                                </div>
                                <div className="col-span-2 grid grid-cols-2 text-sm text-gray-600">
                                  <div>
                                    <p>Quote ID: {activeQuoteId}</p>
                                    <p>Distance: {rateDetails.miles} miles</p>
                                    <p>Travel Time: {rateDetails.travelTime}</p>
                                    <p>States Crossed: {rateDetails.statesCrossed}</p>
                                  </div>
                                  {rateDetails.chassis && (
                                    <div className="text-right">
                                      <p>{rateDetails.chassis.chassisType} Chassis</p>
                                      <p>{rateDetails.chassisDays} days @ ${rateDetails.chassis.rate}/day</p>
                                      <p>*{rateDetails.chassis.minDays} days min.</p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Add CopyPricingForm component */}
                            <div className="mt-4">
                              <CopyPricingForm 
                                rateDetails={rateDetails}
                                quoteId={activeQuoteId}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                    {/* REMOVE bookingcomplete with !bookingComplete to display */}
                    {/* Full Width Booking Section */}
                    {activeQuoteId && bookingComplete && (
                      <div id="booking-section" className="w-full mt-8">
                        <BookingForm
                          quoteId={activeQuoteId}
                          onBookingComplete={handleBookingComplete}
                        />
                      </div>
                    )}
                    
                    {/* Booking Confirmation */}
                    {bookingComplete && (
                      <div className="w-full card bg-green-50 text-center p-6 rounded-lg mt-8">
                        <div className="rounded-full bg-green-100 w-12 h-12 flex items-center justify-center mx-auto mb-3">
                          <CheckCircleIcon className="w-6 h-6 text-green-600" />
                        </div>
                        <h2 className="text-xl font-bold text-green-700 mb-2">
                          Booking Confirmed!
                        </h2>
                        <p className="text-green-600">
                          Your shipment has been successfully booked. You can start a new quote above if needed.
                        </p>
                      </div>
                    )}
                  </div>
                } />
                <Route path="/about" element={<AboutPage />} />
              </Routes>
            </main>

            <footer className="bg-white border-t border-gray-200 flex-shrink-0">
              <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
                <div className="text-center text-gray-500 text-sm">
                  <p>© 2024 LCG Freight. All rights reserved.</p>
                  {!isRatesSubdomain && (
                    <p className="mt-1">Need assistance? Contact our support team</p>
                  )}
                </div>
              </div>
            </footer>
          </div>
        </LoadScript>
      </Router>
    </QueryClientProvider>
  );
}

export default App;
